var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { YMap, YMapDefaultSchemeLayer, YMapListener, YMapDefaultFeaturesLayer, YMapControls, YMapMarker, YMapFeatureDataSource, YMapLayer } from '@yandex/ymaps3-types';
const defaultLocation = {
    center: [39.722852, 43.585475],
    zoom: 15
};
const locationBackground = `
    <svg width="38" height="45" viewBox="0 0 38 45" fill="none" xmlns="http://www.w3.org/2000/svg" style="fill: var(--app-part-main-color);">
        <path d="M37.6231 18.6946C37.6231 31.896 24.8966 41.1755 20.4952 43.99C19.5866 44.571 18.4581 44.5584 17.5603 43.961C13.176 41.0436 0.415527 31.4562 0.415527 18.6946C0.415527 8.42002 8.74473 0.0908203 19.0193 0.0908203C29.2939 0.0908203 37.6231 8.42002 37.6231 18.6946Z" />
    </svg>
`;
const mapStyle = [
    {
        "tags": {
            "any": [
                "road"
            ]
        },
        "elements": "geometry",
        "stylers": [
            {
                "color": "#4D4D4D",
                "opacity": 1,
                "scale": 0.5
            }
        ]
    },
    {
        "tags": {
            "any": [
                "water"
            ]
        },
        "elements": "geometry",
        "stylers": [
            {
                "color": "#93BAC3"
            }
        ]
    },
    {
        "tags": {
            "any": [
                "landscape",
                "admin",
                "land",
                "transit"
            ]
        },
        "elements": "geometry",
        "stylers": [
            {
                "color": "#F0ECE4"
            }
        ]
    },
    {
        "tags": {
            "any": [
                "building"
            ]
        },
        "elements": "geometry",
        "stylers": [
            {
                "color": "#EAE6DF"
            }
        ]
    }
];
const clusterSourceId = 'clusterer-source';
export function createYandexMapProvider(dotNetYandexMapProvider) {
    return __awaiter(this, void 0, void 0, function* () {
        yield ymaps3.ready;
        return new YandexMapProvider(dotNetYandexMapProvider);
    });
}
class YandexMapProvider {
    constructor(dotNetYandexMapProvider) {
        this.handleBehaviorMapEvent = (event) => __awaiter(this, void 0, void 0, function* () {
            yield this.dotNetYandexMapProvider.invokeMethodAsync('UpdateMapState', {
                center: {
                    latitude: event.location.center[1],
                    longitude: event.location.center[0],
                },
                bound: {
                    topLeft: {
                        latitude: event.location.bounds[0][1],
                        longitude: event.location.bounds[0][0]
                    },
                    bottomRight: {
                        latitude: event.location.bounds[1][1],
                        longitude: event.location.bounds[1][0]
                    }
                }
            });
        });
        this.handleUpdateMapEvent = (update) => {
            if (this.centerMarker != null) {
                this.centerMarker.update({
                    coordinates: update.location.center,
                });
            }
        };
        this.dotNetYandexMapProvider = dotNetYandexMapProvider;
    }
    initializeMap(elementId, useCenterMarker) {
        return __awaiter(this, void 0, void 0, function* () {
            const { YMapDefaultMarker } = yield ymaps3.import('@yandex/ymaps3-markers@0.0.1');
            const { YMapClusterer, clusterByGrid } = yield ymaps3.import('@yandex/ymaps3-clusterer@0.0.1');
            const { YMapGeolocationControl } = yield ymaps3.import('@yandex/ymaps3-controls@0.0.1');
            let customization = mapStyle;
            const htmlElement = document.getElementById(elementId);
            this.map = new YMap(htmlElement, { location: defaultLocation }, [
                new YMapDefaultSchemeLayer({
                    customization
                }),
                new YMapDefaultFeaturesLayer({}),
                new YMapFeatureDataSource({
                    id: clusterSourceId
                }),
                new YMapLayer({
                    source: clusterSourceId,
                    type: 'markers',
                    zIndex: 1800
                })
            ]);
            if (useCenterMarker) {
                const contentPin = document.createElement('div');
                contentPin.innerHTML = `<img src="_content/Dvizh.UI/images/locationDetails.svg" class="pin">`;
                this.centerMarker = new YMapMarker({
                    coordinates: defaultLocation.center,
                }, contentPin.cloneNode(true));
                this.map.addChild(this.centerMarker);
            }
            this.map.addChild(new YMapListener({
                onUpdate: this.handleUpdateMapEvent.bind(this),
                onActionEnd: this.handleBehaviorMapEvent.bind(this)
            }));
            this.clusterer = new YMapClusterer({
                method: clusterByGrid({ gridSize: 64 }),
                features: [],
                marker: this.createClustererMarker.bind(this),
                cluster: this.createCluster.bind(this)
            });
            this.map.addChild(this.clusterer);
            this.map.addChild(new YMapControls({ position: 'left' })
                .addChild(new YMapGeolocationControl({})));
            const bounds = this.map.bounds;
            yield this.dotNetYandexMapProvider.invokeMethodAsync('UpdateMapState', {
                center: {
                    latitude: defaultLocation.center[1],
                    longitude: defaultLocation.center[0],
                },
                bound: {
                    topLeft: {
                        latitude: bounds[0][1],
                        longitude: bounds[0][0]
                    },
                    bottomRight: {
                        latitude: bounds[1][1],
                        longitude: bounds[1][0]
                    }
                }
            });
        });
    }
    setMapPoints(points) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clusterer.update({
                features: points.map(point => ({
                    id: point.objectId.toString(),
                    type: `Feature`,
                    geometry: {
                        type: `Point`,
                        coordinates: [point.longitude, point.latitude]
                    },
                    properties: {
                        type: point.type,
                        thumbnailImageUrl: point.thumbnailImageUrl,
                        objectId: point.objectId,
                        thumbnailImageUrls: point.thumbnailImageUrls,
                    }
                }))
            });
        });
    }
    setMapCenter(point) {
        return __awaiter(this, void 0, void 0, function* () {
            this.map.update({
                location: {
                    center: [point.longitude, point.latitude],
                    zoom: 18
                }
            });
            const bounds = this.map.bounds;
            yield this.dotNetYandexMapProvider.invokeMethodAsync('UpdateMapState', {
                center: {
                    latitude: point.latitude,
                    longitude: point.longitude,
                },
                bound: {
                    topLeft: {
                        latitude: bounds[0][1],
                        longitude: bounds[0][0]
                    },
                    bottomRight: {
                        latitude: bounds[1][1],
                        longitude: bounds[1][0]
                    }
                }
            });
        });
    }
    createClustererMarker(feature) {
        const contentPin = this.createMarkerHtml(feature.properties);
        return new YMapMarker({
            coordinates: feature.geometry.coordinates,
            source: clusterSourceId,
            onClick: (event) => __awaiter(this, void 0, void 0, function* () {
                yield this.dotNetYandexMapProvider.invokeMethodAsync('OnPointClick', feature.properties.objectId);
            })
        }, contentPin.cloneNode(true));
    }
    createMarkerHtml(properties) {
        switch (properties.type) {
            case 1:
                return this.createDefaultMarkerImage("_content/Dvizh.UI/images/locationDetails.svg");
            case 2:
                return this.createSubscribedMarker(properties.thumbnailImageUrl);
            case 3:
                return this.createSearchDiscountMakrer(properties.thumbnailImageUrls);
            default:
                return this.createDefaultMarkerImage("_content/Dvizh.UI/images/locationPoint.svg");
        }
    }
    createDefaultMarkerImage(iconPath) {
        const contentPin = document.createElement('div');
        contentPin.innerHTML = `<img src="${iconPath}" class="pin">`;
        return contentPin;
    }
    createSubscribedMarker(thumbnailImageUrl) {
        const contentPin = document.createElement('div');
        contentPin.className = "event-thumbnail_container";
        contentPin.innerHTML = `
            ${locationBackground}
            <img src="${thumbnailImageUrl}" class="event-thumbnail_pin" />
        `;
        return contentPin;
    }
    createSearchDiscountMakrer(thumbnailImageUrls) {
        let images = [];
        let groupStyle = `left: 0px;`;
        if (0 in thumbnailImageUrls) {
            images.push(`<img src="${thumbnailImageUrls[0]}" class="discount-thumbnail_img" style="z-index: 3;" />`);
        }
        if (1 in thumbnailImageUrls) {
            images.push(`<img src="${thumbnailImageUrls[1]}" class="discount-thumbnail_img" style="z-index: 2; margin-left: -20px;" />`);
            groupStyle = `left: -6px;`;
        }
        if (2 in thumbnailImageUrls) {
            images.push(`<img src="${thumbnailImageUrls[2]}" class="discount-thumbnail_img" style="z-index: 1; margin-left: -20px;" />`);
            groupStyle = `left: -12px;`;
        }
        const contentPin = document.createElement('div');
        contentPin.className = "discount-thumbnail_container";
        contentPin.innerHTML = `
            ${locationBackground}
            <div class="discount-thumbnail_group" style="${groupStyle}">
                ${images.join(' ')}
            </div>
        `;
        return contentPin;
    }
    createClustererCircle(count) {
        const circle = document.createElement('div');
        circle.classList.add('circle');
        circle.innerHTML = `
                <div class="circle-content">
                    <span class="circle-text">${count}</span>
                </div>
            `;
        return circle;
    }
    createCluster(coordinates, features) {
        return new YMapMarker({
            coordinates,
            source: clusterSourceId,
            onClick: (event) => __awaiter(this, void 0, void 0, function* () {
                yield this.dotNetYandexMapProvider.invokeMethodAsync('OnClusterClick', features.map(feature => feature.properties.objectId));
            })
        }, this.createClustererCircle(features.length).cloneNode(true));
    }
}
